import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ProjectStats = ({ stats }) => {
  // Create stats array from the passed stats object or use defaults
  const createStatsArray = (statsData) => {
    if (Array.isArray(statsData)) {
      return statsData; // If it's already an array, use it
    }

    if (statsData && typeof statsData === 'object') {
      // Convert object properties to stats array format
      return [
        {
          id: 1,
          label: 'Total Projects',
          value: statsData?.totalProjects?.toString() || '47+',
          icon: 'Briefcase',
          color: 'success',
          description: 'Successfully completed projects across various industries'
        },
        {
          id: 2,
          label: 'Completed Projects',
          value: statsData?.completedProjects?.toString() || '42+',
          icon: 'CheckCircle',
          color: 'accent',
          description: 'Projects delivered on time and within budget'
        },
        {
          id: 3,
          label: 'Client Satisfaction',
          value: `${statsData?.clientSatisfaction || 98}%`,
          icon: 'Heart',
          color: 'warning',
          description: 'Client satisfaction rate based on project feedback'
        },
        {
          id: 4,
          label: 'Years Experience',
          value: `${statsData?.yearOfExperience || 8}+`,
          icon: 'Award',
          color: 'accent',
          description: 'Combined team experience in software development'
        }
      ];
    }

    // Default stats if nothing is provided
    return [
      {
        id: 1,
        label: 'Projects Completed',
        value: '150+',
        icon: 'CheckCircle',
        color: 'success',
        description: 'Successfully delivered projects across various industries'
      },
      {
        id: 2,
        label: 'Happy Clients',
        value: '98%',
        icon: 'Heart',
        color: 'accent',
        description: 'Client satisfaction rate based on project feedback'
      },
      {
        id: 3,
        label: 'Technologies Used',
        value: '25+',
        icon: 'Code',
        color: 'warning',
        description: 'Modern technologies and frameworks in our stack'
      },
      {
        id: 4,
        label: 'Years Experience',
        value: '8+',
        icon: 'Award',
        color: 'accent',
        description: 'Combined team experience in software development'
      }
    ];
  };

  const displayStats = createStatsArray(stats);

  const getColorClasses = (color) => {
    const colorMap = {
      success: 'text-success bg-success/10 border-success/30',
      accent: 'text-accent bg-accent/10 border-accent/30',
      warning: 'text-warning bg-warning/10 border-warning/30',
      error: 'text-error bg-error/10 border-error/30'
    };
    return colorMap?.[color] || colorMap?.accent;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {displayStats?.map((stat, index) => (
        <motion.div
          key={stat?.id}
          initial={{ opacity: 0, y: 30, rotateX: -15 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          whileHover={{ 
            y: -5, 
            rotateX: 5,
            transition: { duration: 0.3 }
          }}
          className="group relative bg-card rounded-2xl p-6 shadow-3d hover:shadow-accent border border-border/50 transform-3d perspective-1000"
        >
          {/* Background Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-accent/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Icon */}
          <div className="relative mb-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${getColorClasses(stat?.color)} group-hover:scale-110 transition-transform duration-300`}>
              <Icon name={stat?.icon} size={24} />
            </div>
          </div>

          {/* Value */}
          <div className="relative mb-2">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
              className="text-3xl font-bold text-foreground group-hover:text-accent transition-colors duration-300"
            >
              {stat?.value}
            </motion.div>
          </div>

          {/* Label */}
          <h3 className="relative text-sm font-semibold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">
            {stat?.label}
          </h3>

          {/* Description */}
          <p className="relative text-xs text-muted-foreground leading-relaxed">
            {stat?.description}
          </p>

          {/* Hover Effect Border */}
          <div className="absolute inset-0 rounded-2xl border border-transparent group-hover:border-accent/30 transition-colors duration-300 pointer-events-none" />

          {/* 3D Depth Indicator */}
          <div className="absolute -bottom-1 -right-1 w-full h-full bg-accent/5 rounded-2xl -z-10 group-hover:bg-accent/10 transition-colors duration-300" />
        </motion.div>
      ))}
    </div>
  );
};

export default ProjectStats;